import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { ColorPicker } from '@/components/common/ColorPicker'
import type { CreateStatusLevelModel, StatusLevelModel } from '@/api/model'
import {
  parseIntegerInput,
  validateStatusLevel,
  type StatusLevelValidationErrors,
} from './statusLevelValidation'

const DEFAULT_LEVEL_COLOR = '#FFD700'

interface Props {
  onClose: () => void
  onSave: (data: CreateStatusLevelModel, id: string | undefined) => void
  isSaving?: boolean
  /** Level to edit; missing = create. */
  level?: StatusLevelModel
  /** The *other* levels — for the overlap check. */
  siblings: StatusLevelModel[]
}

/**
 * Create/edit a status level in a dialog (one component for both, distinguished
 * via `level`). Numeric fields are kept as strings and parsed with
 * `parseIntegerInput`; empty `maxPoints` = open upper end (∞). Validation runs
 * purely client-side beforehand (`validateStatusLevel`), the backend re-validates.
 * Color via the shared `ColorPicker` (no react-color).
 */
export function StatusLevelDialog({ onClose, onSave, isSaving, level, siblings }: Props) {
  const { t } = useTranslation(['service-modules-status', 'common'])

  const [name, setName] = useState(level?.name ?? '')
  const [colorHex, setColorHex] = useState(level?.colorHex ?? DEFAULT_LEVEL_COLOR)
  const [payoutPoints, setPayoutPoints] = useState(level?.payoutPoints.toString() ?? '')
  const [minPoints, setMinPoints] = useState(level?.minPoints.toString() ?? '')
  const [maxPoints, setMaxPoints] = useState(level?.maxPoints?.toString() ?? '')
  const [errors, setErrors] = useState<StatusLevelValidationErrors>({})

  const isEdit = !!level
  const maxPointsIsOpenEnded = maxPoints.trim().length === 0

  const handleSave = () => {
    const candidate = {
      name,
      payoutPoints: parseIntegerInput(payoutPoints),
      minPoints: parseIntegerInput(minPoints),
      maxPoints: maxPointsIsOpenEnded ? null : parseIntegerInput(maxPoints),
      maxPointsIsOpenEnded,
    }
    const validationErrors = validateStatusLevel(candidate, siblings)
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return

    onSave(
      {
        name: name.trim(),
        colorHex,
        payoutPoints: candidate.payoutPoints as number,
        minPoints: candidate.minPoints as number,
        maxPoints: candidate.maxPoints,
      },
      level?.id,
    )
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('dialog.editTitle') : t('dialog.createTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="status-level-name">{t('dialog.nameLabel')}</Label>
            <Input
              id="status-level-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-invalid={!!errors.name}
            />
            {errors.name && <p className="text-xs text-destructive">{t(errors.name)}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('dialog.colorLabel')}</Label>
            <ColorPicker value={colorHex} onChange={setColorHex} />
          </div>

          <div className="flex gap-4">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="status-level-min">{t('dialog.minPointsLabel')}</Label>
              <Input
                id="status-level-min"
                inputMode="numeric"
                value={minPoints}
                onChange={(e) => setMinPoints(e.target.value)}
                aria-invalid={!!errors.minPoints}
              />
              {errors.minPoints && <p className="text-xs text-destructive">{t(errors.minPoints)}</p>}
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="status-level-max">{t('dialog.maxPointsLabel')}</Label>
              <Input
                id="status-level-max"
                inputMode="numeric"
                placeholder="∞"
                value={maxPoints}
                onChange={(e) => setMaxPoints(e.target.value)}
                aria-invalid={!!errors.maxPoints}
              />
              {errors.maxPoints ? (
                <p className="text-xs text-destructive">{t(errors.maxPoints)}</p>
              ) : maxPointsIsOpenEnded ? (
                <p className="text-xs text-muted-foreground">{t('dialog.openEndedHint')}</p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="status-level-payout">{t('dialog.payoutPointsLabel')}</Label>
            <Input
              id="status-level-payout"
              inputMode="numeric"
              value={payoutPoints}
              onChange={(e) => setPayoutPoints(e.target.value)}
              aria-invalid={!!errors.payoutPoints}
            />
            {errors.payoutPoints && <p className="text-xs text-destructive">{t(errors.payoutPoints)}</p>}
          </div>

          {errors.range && <p className="text-xs text-destructive">{t(errors.range)}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {t('buttons.save', { ns: 'common' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
